import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Pagination,
  PaginationOpts,
  TableHeader,
  TableBody,
  Filter,
  useDatatableLifecycle,
  shouldTableUpdate
} from 'react-bs-datatable';

import './CustomDataTable.css';

const CustomDataTable = React.memo(props => {
  const { persistKey: customPersistKey, persistKeySuffix, disablePersistence } = props;
  const location = useLocation();
  const locationPathname = location && typeof location.pathname === 'string' ? location.pathname : '';
  const storageAvailable =
    typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

  const persistenceKey = useMemo(() => {
    if (disablePersistence) {
      return null;
    }

    const baseKeyCandidate =
      typeof customPersistKey === 'string' && customPersistKey.trim().length > 0
        ? customPersistKey.trim()
        : locationPathname;

    if (!baseKeyCandidate) {
      return null;
    }

    const suffix =
      typeof persistKeySuffix === 'string' && persistKeySuffix.trim().length > 0
        ? `:${persistKeySuffix.trim()}`
        : '';

    return `datatable:${baseKeyCandidate}${suffix}`;
  }, [customPersistKey, disablePersistence, locationPathname, persistKeySuffix]);

  const rowsStorageKey = persistenceKey ? `${persistenceKey}:rowsPerPage` : null;
  const pageStorageKey = persistenceKey ? `${persistenceKey}:page` : null;
  const hydrationRef = useRef({ rows: false, page: false });

  const {
    data,
    rowsPerPageOption,
    tableHeaders,
    onChangeFilter,
    onPageNavigate,
    classes,
    onRowsPerPageChange,
    onSortChange,
    tableClass,
    labels,
    filterable,
    filterText,
    rowsPerPage,
    currentPage,
    sortedProp,
    maxPage,
    Components
  } = useDatatableLifecycle(props);

  useEffect(() => {
    if (!storageAvailable || !persistenceKey) {
      return;
    }

    const hydrationState = hydrationRef.current;

    if (!hydrationState.rows) {
      const storedRowsRaw = rowsStorageKey ? window.sessionStorage.getItem(rowsStorageKey) : null;
      hydrationState.rows = true;

      if (storedRowsRaw !== null) {
        const parsedRows = parseInt(storedRowsRaw, 10);
        if (!Number.isNaN(parsedRows) && parsedRows > 0 && parsedRows !== rowsPerPage) {
          onRowsPerPageChange(parsedRows);
          return;
        }
      }
    }

    if (!hydrationState.page) {
      const storedPageRaw = pageStorageKey ? window.sessionStorage.getItem(pageStorageKey) : null;
      hydrationState.page = true;

      if (storedPageRaw !== null) {
        const parsedPage = parseInt(storedPageRaw, 10);
        if (!Number.isNaN(parsedPage) && parsedPage > 0) {
          const maxPageNumber = typeof maxPage === 'number' && maxPage > 0 ? maxPage : null;
          const targetPage = maxPageNumber && parsedPage > maxPageNumber ? maxPageNumber : parsedPage;

          if (targetPage && targetPage !== currentPage) {
            onPageNavigate(targetPage);
          }
        }
      }
    }
  }, [
    storageAvailable,
    persistenceKey,
    rowsStorageKey,
    pageStorageKey,
    rowsPerPage,
    currentPage,
    maxPage,
    onRowsPerPageChange,
    onPageNavigate
  ]);

  useEffect(() => {
    if (!storageAvailable || !persistenceKey || !hydrationRef.current.rows || !rowsStorageKey) {
      return;
    }

    window.sessionStorage.setItem(rowsStorageKey, String(rowsPerPage));
  }, [storageAvailable, persistenceKey, rowsStorageKey, rowsPerPage]);

  useEffect(() => {
    if (!storageAvailable || !persistenceKey || !hydrationRef.current.page || !pageStorageKey) {
      return;
    }

    window.sessionStorage.setItem(pageStorageKey, String(currentPage));
  }, [storageAvailable, persistenceKey, pageStorageKey, currentPage]);

  return (
    <Fragment>
      <Components.Row className="controlRow__root">
        <Components.Col xs="12">
          <Filter
            classes={classes}
            tableHeaders={tableHeaders}
            placeholder={labels.filterPlaceholder}
            onChangeFilter={onChangeFilter}
            filterText={filterText}
            filterable={filterable}
            components={{
              Adornment: Components.Adornment,
              Button: Components.Button,
              ClearIcon: Components.ClearIcon,
              FormControl: Components.FormControl,
              InputGroup: Components.InputGroup
            }}
          />
        </Components.Col>
      </Components.Row>
      <div className='table-responsive'>
        <Components.Row>
          <Components.Col xs="12">
            <Components.Table className={tableClass}>
              <TableHeader
                classes={classes}
                tableHeaders={tableHeaders}
                sortedProp={sortedProp}
                onSortChange={onSortChange}
                components={{
                  TableHead: Components.TableHead,
                  TableCell: Components.TableCell,
                  TableRow: Components.TableRow
                }}
              />
              <TableBody
                classes={classes}
                tableHeaders={tableHeaders}
                labels={labels}
                data={data}
                components={{
                  TableBody: Components.TableBody,
                  TableCell: Components.TableCell,
                  TableRow: Components.TableRow
                }}
              />
            </Components.Table>
          </Components.Col>
        </Components.Row>
      </div>
      <Components.Row className="controlRow__root bottom">
        <Components.Col xs={12} md={4} />
        <Components.Col xs={12} md={4}>
          <PaginationOpts
            classes={classes}
            labels={labels}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPage={rowsPerPage}
            rowsPerPageOption={rowsPerPageOption}
            components={{
              Form: Components.Form,
              FormGroup: Components.FormGroup,
              FormControl: Components.FormControl
            }}
          />
        </Components.Col>
        <Components.Col xs={12} md={4} className="text-right">
          <Pagination
            classes={classes}
            data={data}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            onPageNavigate={onPageNavigate}
            labels={labels}
            maxPage={maxPage}
            components={{
              Button: Components.Button,
              ButtonGroup: Components.ButtonGroup
            }}
          />
        </Components.Col>
      </Components.Row>
    </Fragment>
  );
}, shouldTableUpdate);

export default CustomDataTable;
